import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  localISODate,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Payment = {
  id: number
  number: string
  partner: number
  partner_name: string
  payment_date: string
  bank_account: number
  bank_account_code: string
  amount: string
  currency_code: string
  payment_method: 'WIRE' | 'CHECK' | 'CASH' | 'CARD' | 'OTHER'
  status: 'DRAFT' | 'POSTED' | 'VOID'
  journal_entry: number | null
  journal_entry_number: string | null
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }
type Account = { id: number; code: string; name: string; account_type: string; is_active: boolean }

// Minimal create payload — rest filled in on the record page.
type CreateInput = {
  number: string
  partner: number | 0
  payment_date: string
  bank_account: number | 0
  amount: string
  currency_code: string
  payment_method: Payment['payment_method']
  reference: string
}

const STATUS_COLOR: Record<Payment['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  POSTED: 'bg-green-100 text-green-800',
  VOID: 'bg-red-100 text-red-700',
}

const METHOD_LABEL: Record<Payment['payment_method'], string> = {
  WIRE: 'Bank transfer',
  CHECK: 'Check',
  CASH: 'Cash',
  CARD: 'Card',
  OTHER: 'Other',
}

const STATUS_FILTERS: ('ALL' | Payment['status'])[] = ['ALL', 'DRAFT', 'POSTED', 'VOID']

export default function FinancePayments() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const paymentsQ = useQuery({
    queryKey: ['finance', 'payments'],
    queryFn: async () => (await api.get<Payment[]>('/finance/payments/')).data,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/finance/accounts/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterStatus, setFilterStatus] = useState<'ALL' | Payment['status']>('ALL')

  function openCreate() {
    const defaultBank = bankAccounts[0]
    setDraft({
      ...emptyDraft(),
      number: `PMT-${Date.now().toString().slice(-6)}`,
      bank_account: defaultBank?.id ?? 0,
    })
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Payment>('/finance/payments/', {
      ...draft,
      partner: Number(draft.partner),
      bank_account: Number(draft.bank_account),
      status: 'DRAFT',
      notes: '',
      allocations: [],
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['finance', 'payments'] })
      toast.success('Payment created')
      setCreating(false)
      navigate(`/admin/finance/payments/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/finance/payments/${id}/`),
    onSuccess: () => {
      toast.success('Payment deleted')
      qc.invalidateQueries({ queryKey: ['finance', 'payments'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const postMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/finance/payments/${id}/post_to_gl/`)).data,
    onSuccess: () => {
      toast.success('Posted to General Ledger')
      qc.invalidateQueries({ queryKey: ['finance', 'payments'] })
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
      qc.invalidateQueries({ queryKey: ['finance', 'journal-entries'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const payments = paymentsQ.data || []
  const customers = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'CUSTOMER' || p.partner_type === 'BOTH')
  )
  const bankAccounts = (accountsQ.data || []).filter(
    (a) => a.is_active && a.account_type === 'ASSET' && a.code.startsWith('11')
  )
  const filtered = filterStatus === 'ALL'
    ? payments
    : payments.filter((p) => p.status === filterStatus)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Payments</h2>
          <p className="text-sm text-muted-foreground">
            {payments.length} total · {payments.filter((p) => p.status === 'POSTED').length} posted
          </p>
        </div>
        <Button onClick={openCreate} disabled={customers.length === 0 || bankAccounts.length === 0}>
          <Plus className="h-3 w-3 mr-1" /> New payment
        </Button>
      </div>

      {(customers.length === 0 || bankAccounts.length === 0) && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-lg mb-4">
          {customers.length === 0 && <>Add at least one active <strong>customer</strong>. </>}
          {bankAccounts.length === 0 && <>Add a bank account (ASSET account with code starting with 11).</>}
        </div>
      )}

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {paymentsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No payments.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Number</Th>
                <Th>Date</Th>
                <Th>Customer</Th>
                <Th>Bank</Th>
                <Th>Method</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/finance/payments/${p.id}`)}
                >
                  <Td className="font-mono text-xs">{p.number}</Td>
                  <Td className="text-xs">{p.payment_date}</Td>
                  <Td>{p.partner_name}</Td>
                  <Td className="font-mono text-xs">{p.bank_account_code}</Td>
                  <Td className="text-xs">{METHOD_LABEL[p.payment_method]}</Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[p.status]}`}>{p.status}</span>
                      {p.journal_entry_number && (
                        <span className="text-xs text-muted-foreground font-mono">{p.journal_entry_number}</span>
                      )}
                    </div>
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/finance/payments/${p.id}`)}>
                          Open
                        </DropdownMenuItem>
                        {p.status === 'DRAFT' && !p.journal_entry && (
                          <DropdownMenuItem
                            onClick={() => confirm(
                              `Post payment ${p.number} to General Ledger`,
                              () => postMut.mutate(p.id),
                            )}
                            disabled={postMut.isPending}
                            className="text-green-700"
                          >
                            Post to GL
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={!!p.journal_entry}
                          onClick={() => !p.journal_entry && confirm(
                            `payment ${p.number}`,
                            () => deleteMut.mutate(p.id),
                          )}
                          className={!p.journal_entry ? 'text-destructive' : ''}
                        >
                          {p.journal_entry ? 'Delete (posted)' : 'Delete'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Allocations + notes on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New payment"
        description="Record the payment header. Allocate it to specific invoices on the record page, then post to the General Ledger."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="pm-num">Number *</FieldLabel>
              <Input id="pm-num" autoFocus required value={draft.number}
                onChange={(ev) => setDraft({ ...draft, number: ev.target.value })}
                className="font-mono" />
            </Field>
            <Field>
              <FieldLabel htmlFor="pm-date">Payment date *</FieldLabel>
              <DatePicker id="pm-date" required value={draft.payment_date}
                onChange={(v) => setDraft({ ...draft, payment_date: v })} />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="pm-customer">Customer *</FieldLabel>
            <SearchableSelect
              id="pm-customer"
              value={draft.partner ? String(draft.partner) : undefined}
              onChange={(v) => setDraft({ ...draft, partner: Number(v) })}
              placeholder="— select —"
              options={customers.map((p) => ({
                value: String(p.id), label: p.name, hint: p.code,
              }))}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="pm-bank">Bank account *</FieldLabel>
            <SearchableSelect
              id="pm-bank"
              value={draft.bank_account ? String(draft.bank_account) : undefined}
              onChange={(v) => setDraft({ ...draft, bank_account: Number(v) })}
              placeholder="— select —"
              options={bankAccounts.map((a) => ({
                value: String(a.id), label: a.name, hint: a.code,
              }))}
            />
            <FieldDescription>ASSET accounts with codes starting with 11.</FieldDescription>
          </Field>

          <div className="grid grid-cols-3 gap-4">
            <Field>
              <FieldLabel htmlFor="pm-amount">Amount</FieldLabel>
              <Input id="pm-amount" type="number" step="0.01" value={draft.amount}
                onChange={(ev) => setDraft({ ...draft, amount: ev.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="pm-cur">Currency</FieldLabel>
              <Input id="pm-cur" value={draft.currency_code}
                onChange={(ev) => setDraft({ ...draft, currency_code: ev.target.value.toUpperCase().slice(0, 3) })}
                className="font-mono" maxLength={3} />
            </Field>
            <Field>
              <FieldLabel htmlFor="pm-method">Method</FieldLabel>
              <Select value={draft.payment_method}
                onValueChange={(v) => setDraft({ ...draft, payment_method: v as Payment['payment_method'] })}>
                <SelectTrigger id="pm-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(METHOD_LABEL) as Payment['payment_method'][]).map((k) => (
                    <SelectItem key={k} value={k}>{METHOD_LABEL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="pm-ref">Reference</FieldLabel>
            <Input id="pm-ref" value={draft.reference}
              onChange={(ev) => setDraft({ ...draft, reference: ev.target.value })}
              placeholder="wire reference, check #, etc."
              className="font-mono" />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    number: '',
    partner: 0,
    payment_date: localISODate(new Date()),
    bank_account: 0,
    amount: '0',
    currency_code: 'EUR',
    payment_method: 'WIRE',
    reference: '',
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
